import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  House, ArrowUp, ArrowDown, Minus, ArrowsClockwise, Users, Tag, Clock, ChartLineUp, TrendUp, TrendDown
} from "@phosphor-icons/react";
import CpiChart from "@/components/CpiChart";

function fmtK(val) {
  if (!val) return "$0";
  if (val >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val.toLocaleString()}`;
}

function MarketBadge({ market }) {
  if (!market) return null;
  const colors = {
    seller: "bg-red-900/40 text-red-400 border-red-800/40",
    buyer: "bg-emerald-900/40 text-emerald-400 border-emerald-800/40",
    neutral: "bg-zinc-800 text-zinc-400 border-zinc-700",
  };
  return (
    <Badge className={`text-[9px] px-2 py-0.5 rounded-sm border ${colors[market.bias] || colors.neutral}`} data-testid="market-badge">
      {market.label}
    </Badge>
  );
}

function StatBox({ label, value, sub, color = "text-zinc-200" }) {
  return (
    <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-3 text-center">
      <span className="text-[8px] uppercase tracking-wider text-zinc-500 block mb-1">{label}</span>
      <span className={`text-lg font-bold font-data block ${color}`}>{value}</span>
      {sub && <span className="text-[9px] text-zinc-600 block mt-0.5">{sub}</span>}
    </div>
  );
}

function BuyerSellerBar({ sold_above_pct, price_cuts_pct }) {
  const sellerW = Math.min(sold_above_pct, 100);
  const buyerW = Math.min(price_cuts_pct, 100);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-[9px]">
        <span className="text-red-400 flex items-center gap-1"><TrendUp size={9} /> Sold Above List: {sold_above_pct}%</span>
        <span className="text-emerald-400 flex items-center gap-1"><TrendDown size={9} /> Price Cuts: {price_cuts_pct}%</span>
      </div>
      <div className="flex h-2 rounded-sm overflow-hidden bg-zinc-800">
        <div className="bg-red-500/60 transition-all" style={{ width: `${sellerW}%` }} title={`${sold_above_pct}% sold above list`} />
        <div className="flex-1" />
        <div className="bg-emerald-500/60 transition-all" style={{ width: `${buyerW}%` }} title={`${price_cuts_pct}% with price cuts`} />
      </div>
      <div className="flex items-center justify-between text-[8px] text-zinc-600">
        <span>Seller Pressure</span>
        <span>Buyer Pressure</span>
      </div>
    </div>
  );
}

function CityCard({ metro, isSelected, onSelect }) {
  const yoyColor = metro.yoy_change > 0 ? "text-emerald-400" : metro.yoy_change < 0 ? "text-red-400" : "text-zinc-400";
  return (
    <button
      onClick={() => onSelect(metro.id)}
      data-testid={`city-${metro.id}`}
      className={`text-left w-full bg-zinc-900/50 border rounded-sm p-4 transition-all hover:border-amber-500/40 ${
        isSelected ? "border-amber-500/50 bg-amber-500/5" : "border-amber-500/15"
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <span className="text-sm font-medium text-zinc-100">{metro.name}</span>
          <span className="text-[10px] text-zinc-500 ml-1.5">{metro.state}</span>
        </div>
        <MarketBadge market={metro.market_type} />
      </div>
      <div className="flex items-end justify-between">
        <div>
          <span className="text-lg font-bold font-data text-zinc-50">{fmtK(metro.median_price)}</span>
          <span className={`text-[11px] font-data ml-2 ${yoyColor}`}>
            {metro.yoy_change > 0 ? "+" : ""}{metro.yoy_change}% YoY
          </span>
        </div>
        <div className="text-right">
          <span className="text-[10px] text-zinc-500 block">{metro.dom}d on market</span>
          <span className="text-[10px] text-zinc-500">{metro.inventory_months}mo supply</span>
        </div>
      </div>
    </button>
  );
}

export default function RealEstateReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState("nyc");
  const [chartType, setChartType] = useState("housing");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/real-estate`);
      setData(resp.data);
    } catch (e) {
      console.error("Failed to load real estate data:", e);
    } finally {
      setLoading(false);
    }
  };

  const national = data?.national || {};
  const metros = data?.metros || [];
  const selected = metros.find(m => m.id === selectedCity);
  const chartData = chartType === "housing" ? (data?.national_chart || []) : (data?.mortgage_chart || []);

  return (
    <div className="p-5 space-y-5 max-w-[1600px]" data-testid="real-estate-page">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold text-zinc-50 tracking-tight flex items-center gap-2">
            <House size={20} weight="bold" className="text-amber-500" />
            Real Estate Market
          </h2>
          <p className="text-xs text-zinc-500 mt-1">Buyer vs Seller market data across top 20 US metros</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1 border border-amber-500/20 rounded-sm"
          data-testid="re-refresh-btn"
        >
          <ArrowsClockwise size={11} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 bg-zinc-900/60" />)}
          </div>
          <Skeleton className="h-64 bg-zinc-900/60" />
        </div>
      ) : (
        <>
          {/* National Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="national-summary">
            <StatBox label="Avg Median Price" value={fmtK(national.avg_median_price)} />
            <StatBox label="Avg YoY Change" value={`${national.avg_yoy_change > 0 ? "+" : ""}${national.avg_yoy_change}%`} color={national.avg_yoy_change > 0 ? "text-emerald-400" : "text-red-400"} />
            <StatBox label="Avg Days on Market" value={`${national.avg_dom}d`} />
            <StatBox label="Avg Inventory" value={`${national.avg_inventory_months}mo`} />
            <StatBox label="Seller's Markets" value={national.seller_markets} color="text-red-400" sub="of 20 metros" />
            <StatBox label="Buyer's Markets" value={national.buyer_markets} color="text-emerald-400" sub="of 20 metros" />
            <StatBox label="Balanced" value={national.balanced_markets} color="text-zinc-400" sub="of 20 metros" />
          </div>

          {/* Charts */}
          <div className="bg-zinc-900/30 border border-amber-500/15 rounded-sm p-5 space-y-3" data-testid="re-chart-section">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-zinc-200 flex items-center gap-2">
                <ChartLineUp size={14} className="text-amber-400" />
                {chartType === "housing" ? "Real Estate Index (XLRE)" : "Est. 30Y Mortgage Rate"}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setChartType("housing")}
                  data-testid="chart-housing"
                  className={`text-[10px] px-2.5 py-1 rounded-sm border transition-colors ${
                    chartType === "housing" ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "border-zinc-800/60 text-zinc-500 hover:text-zinc-300"
                  }`}
                >Housing Index</button>
                <button
                  onClick={() => setChartType("mortgage")}
                  data-testid="chart-mortgage"
                  className={`text-[10px] px-2.5 py-1 rounded-sm border transition-colors ${
                    chartType === "mortgage" ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "border-zinc-800/60 text-zinc-500 hover:text-zinc-300"
                  }`}
                >Mortgage Rate</button>
              </div>
            </div>
            <div className="h-56" data-testid="re-chart">
              {chartData.length > 0 ? (
                <CpiChart data={chartData} color={chartType === "housing" ? "#f59e0b" : "#ef4444"} label={chartType === "housing" ? "XLRE Index" : "Est. Mortgage %"} />
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-600 text-sm">No chart data</div>
              )}
            </div>
          </div>

          {/* Selected City Detail */}
          {selected && (
            <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-5 space-y-4" data-testid="city-detail">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-zinc-100">{selected.name}, {selected.state}</h3>
                  <MarketBadge market={selected.market_type} />
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold font-data text-zinc-50">{fmtK(selected.median_price)}</span>
                  <span className={`text-sm font-data ml-2 ${selected.yoy_change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {selected.yoy_change >= 0 ? "+" : ""}{selected.yoy_change}% YoY
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatBox label="Days on Market" value={`${selected.dom}d`} sub={selected.dom < 30 ? "Fast moving" : selected.dom > 50 ? "Slow market" : "Average pace"} />
                <StatBox label="Inventory Supply" value={`${selected.inventory_months}mo`} sub={selected.inventory_months < 3 ? "Low supply" : selected.inventory_months > 5 ? "High supply" : "Balanced"} />
                <StatBox label="Sold Above List" value={`${selected.sold_above_pct}%`} color={selected.sold_above_pct > 40 ? "text-red-400" : "text-zinc-200"} sub="Seller indicator" />
                <StatBox label="Price Cuts" value={`${selected.price_cuts_pct}%`} color={selected.price_cuts_pct > 20 ? "text-emerald-400" : "text-zinc-200"} sub="Buyer indicator" />
              </div>

              <BuyerSellerBar sold_above_pct={selected.sold_above_pct} price_cuts_pct={selected.price_cuts_pct} />
            </div>
          )}

          {/* Metro Grid */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users size={14} className="text-zinc-500" />
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Top 20 Metro Areas — Select a City</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="metro-grid">
              {metros.map(m => (
                <CityCard key={m.id} metro={m} isSelected={selectedCity === m.id} onSelect={setSelectedCity} />
              ))}
            </div>
          </div>

          {data?.fetched_at && (
            <div className="flex items-center justify-between pt-2 border-t border-amber-500/15" data-testid="re-footer">
              <span className="text-[10px] text-zinc-600">Source: {data.source}</span>
              <span className="text-[10px] text-zinc-600">Updated: {new Date(data.fetched_at).toLocaleString()}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
